import { useRef, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { GraniteLabel } from 'components/V2/Label/GraniteLabel';
import { ErrorSubtext } from 'components/ErrorSubtext/ErrorSubtext';

interface SignaturePadProps {
  value?: string;
  onChange: (val: string) => void;
  error?: string | boolean;
  label?: string;
  subtitle?: string;
  className?: string;
  outerInputClassName?: string;
}

const SignaturePad = ({
  value,
  onChange,
  error,
  label,
  subtitle,
  className,
  outerInputClassName,
}: SignaturePadProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [history, setHistory] = useState<string[]>([]);

  useEffect(() => {
    if (value && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        const img = new Image();
        img.src = value;
        img.onload = () => {
          ctx.clearRect(
            0,
            0,
            canvasRef.current!.width,
            canvasRef.current!.height,
          );
          ctx.drawImage(img, 0, 0);
        };
      }
    }
  }, [value]);

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    ctx.beginPath();
    const { x, y } = getCoords(e);
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoords(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const endDrawing = () => {
    if (!drawing) return;
    setDrawing(false);

    if (canvasRef.current) {
      const dataURL = canvasRef.current.toDataURL('image/png');

      setHistory((prev) => [...prev, dataURL]);

      onChange(dataURL);
    }
  };

  const cancelDrawing = () => {
    if (drawing) {
      setDrawing(false);
    }
  };

  const undo = () => {
    if (history.length === 0 || !canvasRef.current) return;
    const prev = history[history.length - 2];
    const newHistory = history.slice(0, -1);
    setHistory(newHistory);

    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

      if (prev) {
        const img = new Image();
        img.src = prev;
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
          onChange(prev);
        };
      } else {
        onChange('');
      }
    }
  };

  // Ctrl+Z shortcut
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  const getCoords = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();

    if ('touches' in e) {
      const touch = e.touches[0];
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    } else {
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
  };

  return (
    <GraniteLabel
      label={label}
      subtitle={subtitle}
      className={clsx(className, 'relative')}
    >
      <div
        className={clsx(
          outerInputClassName,
          'granite-input granite-input-primary relative grid w-full',
          error && 'error',
        )}
        style={{ height: 48 }}
      >
        <canvas
          ref={canvasRef}
          height={48}
          width={380}
          className="block rounded-md border bg-white"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={endDrawing}
          onMouseLeave={cancelDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={endDrawing}
        />
      </div>
      {error && <ErrorSubtext error={error} />}
    </GraniteLabel>
  );
};

export default SignaturePad;
